import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Typography,
  Autocomplete
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

const VALID_UNITS = [
  'oz', 'ml', 'cl', 'dash', 'drop', 'tsp', 'tbsp', 'barspoon', 'pinch', 'cup'
];

const IngredientFormItem = ({
  ingredient,
  index,
  inventory,
  amountError,
  onRemove,
  onChange,
  onAmountChange,
  canRemove,
  isMobile
}) => {
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        mb: 2, 
        p: 2, 
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative'
      }}
    >
      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        sx={{ 
          position: 'absolute',
          top: 8,
          right: 8
        }}
      >
        <DeleteIcon />
      </IconButton>
      
      {/* Ingredient Name */}
      <Box sx={{ mb: 2, mr: 4 }}>
        <Autocomplete
          freeSolo
          size={isMobile ? "small" : "medium"}
          options={inventory}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (!option) return '';
            return option.name || '';
          }}
          value={ingredient.name}
          onChange={(event, newValue) => {
            const nameValue =
              typeof newValue === 'string'
                ? newValue
                : newValue?.name || '';
            const idValue =
              typeof newValue === 'string'
                ? ''
                : newValue?.id || '';
            
            onChange(index, 'name', nameValue);
            if (typeof newValue !== 'string') {
              onChange(index, 'ingredientId', idValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Ingredient"
              placeholder="Search or type new ingredient"
            />
          )}
          renderOption={(props, option) => {
            const key =
              typeof option === 'string' ? option : option.id;
            if (typeof option === 'string') {
              return (
                <li key={key} {...props}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <AddIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Add "{option}"
                    </Typography>
                  </Box>
                </li>
              );
            }
            return (
              <li key={key} {...props}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Typography variant="body2">
                    {option.name}
                  </Typography>
                </Box>
              </li>
            );
          }}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) {
              return options;
            }
            const filtered = options.filter(option => {
              const name =
                typeof option === 'string'
                  ? option
                  : option.name;
              return name
                .toLowerCase()
                .includes(inputValue.toLowerCase());
            });
            const inputValueLower = inputValue.toLowerCase();
            const isExactMatch = filtered.some(option => {
              const name =
                typeof option === 'string'
                  ? option
                  : option.name;
              return name.toLowerCase() === inputValueLower;
            });
            if (!isExactMatch) {
              filtered.unshift(inputValue);
            }
            return filtered;
          }}
          isOptionEqualToValue={(option, value) => {
            if (typeof option === 'string' && typeof value === 'string') {
              return option.toLowerCase() === value.toLowerCase();
            }
            if (typeof option === 'string') {
              return (
                option.toLowerCase() === value.name.toLowerCase()
              );
            }
            if (typeof value === 'string') {
              return (
                option.name.toLowerCase() === value.toLowerCase()
              );
            }
            return (
              option.id === value.id ||
              option.name.toLowerCase() ===
              value.name.toLowerCase()
            );
          }}
        />
      </Box>
      
      {/* Amount and Unit - side by side */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          size={isMobile ? "small" : "medium"}
          label="Amount"
          value={ingredient.amount}
          onChange={(e) => onAmountChange(index, e.target.value)}
          error={!!amountError}
          helperText={amountError || ''}
          placeholder="e.g., 1, 1.5, 1/2"
          sx={{ flex: 1 }}
        />
        
        <FormControl sx={{ flex: 1 }} size={isMobile ? "small" : "medium"}>
          <InputLabel>Unit</InputLabel>
          <Select
            value={ingredient.unit}
            label="Unit"
            onChange={(e) => onChange(index, 'unit', e.target.value)}
          >
            {VALID_UNITS.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );
};

export default IngredientFormItem; 